import { MinusIcon, PlusIcon } from "@xola/icons";
import { FormGroup, getSymbol, Label, Modal, roundNumber, SubmitButton, Textarea, ToggleButton } from "@xola/ui-kit";
import { FormField, handleSuccessfulSubmission, InputField, nullableString } from "common";
import React from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import {
    LineItemObject,
    LineItemType,
    LineItemVisibility,
    NotificationPreferenceType,
    Purchase,
    PurchaseItem,
    Seller,
} from "types";
import { number, object } from "yup";
import { api } from "../../api/api";
import { useSeller } from "../../hooks/useAccount";
import { useInvalidate } from "../../hooks/useInvalidation";
import { Currency } from "../Common/Currency";
import { NotifyCustomerCheckbox } from "../Common/NotifyCustomerCheckbox";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { CurrencyInputField } from "../Field/CurrencyInputField";

interface FormValues {
    amount: number;
    reason?: string;
    factor: number;
    notifyCustomer: boolean;
}

interface AdjustBalanceModalProps {
    purchase: Purchase;
    item: PurchaseItem;
    isOpen: boolean;
    onClose: () => void;
}

const validationSchema = object({
    amount: number().required().min(0.5, "must be 0.5 at minimum"),
    reason: nullableString().required(),
});

const getNewBalance = (values: FormValues, oldBalance: number) => {
    const balance = oldBalance + (values.amount ?? 0) * values.factor;
    return balance < 0 ? 0 : balance;
};

const isPurchaseModificationNotificationEnabled = (seller: Seller) =>
    seller.preferences.notifications
        .find((preference) => preference.type === NotificationPreferenceType.ORDER_CHANGED_TRAVELER)
        ?.channels.some((channel) => channel.enabled) ?? false;

export const AdjustBalanceModal = ({ purchase, item, isOpen, onClose }: AdjustBalanceModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent purchase={purchase} item={item} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({ purchase, item, onClose }: Except<AdjustBalanceModalProps, "isOpen">) => {
    const invalidate = useInvalidate();
    const seller = useSeller();

    const {
        mutateAsync: adjustGiftBalance,
        isLoading: isSaving,
        isSuccess,
    } = useMutation(async (values: FormValues) => {
        const name =
            values.reason ??
            `Balance ${values.factor === 1 ? "increased" : "decreased"} by ${getSymbol(seller.currency)}${
                values.amount
            }`;
        const payload = {
            object: LineItemObject.DEFAULT,
            type: LineItemType.ADJUSTMENT,
            amount: values.factor * roundNumber(purchase.currency, values.amount),
            name: name,
            visibility: LineItemVisibility.NONE,
            includeInAmount: false,
            _emailOverrides: { sendEmailToTraveler: values.notifyCustomer },
        };

        return api.post(`/purchases/${purchase.id}/items/${item.id}/lineItems`, payload);
    });

    const handleSubmit = async (values: FormValues) => {
        await adjustGiftBalance(values);
        handleSuccessfulSubmission(() => {
            invalidate("purchases", purchase.id);
            onClose();
        });
    };

    const balanceValue = item.balance ?? 0;

    const initialValues: FormValues = {
        amount: 0,
        factor: -1,
        notifyCustomer: isPurchaseModificationNotificationEnabled(seller),
    };

    return (
        <XolaFormik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, isValid, setFieldValue, values }) => (
                <>
                    <Modal.Header>Adjust Balance</Modal.Header>
                    <Modal.Body className="flex-col space-y-4">
                        <FormGroup>
                            <Label>Action</Label>
                            <div className="space-x-2">
                                <ToggleButton
                                    // Those styles added just to prevent onClick styles trigger, if this button is already active
                                    className={values.factor === 1 ? "pointer-events-none" : "pointer-events-auto"}
                                    isActive={values.factor === 1}
                                    onClick={() => {
                                        setFieldValue("factor", 1);
                                    }}
                                >
                                    <span className="space-x-2 font-bold">
                                        <PlusIcon />
                                        <span>Increase</span>
                                    </span>
                                </ToggleButton>
                                <ToggleButton
                                    className={values.factor === -1 ? "pointer-events-none" : "pointer-events-auto"}
                                    isActive={values.factor === -1}
                                    onClick={() => {
                                        setFieldValue("factor", -1);
                                    }}
                                >
                                    <span className="space-x-2 font-bold">
                                        <MinusIcon />
                                        <span>Decrease</span>
                                    </span>
                                </ToggleButton>
                            </div>
                        </FormGroup>
                        <FormField name="amount" label="Amount" className="w-full">
                            <CurrencyInputField isRequired />
                        </FormField>
                        <FormField label="Reason" name="reason">
                            <InputField isRequired as={Textarea} />
                        </FormField>
                        <div
                            className="flex justify-end space-x-2 py-2 font-bold"
                            data-testid="adjust-balance-modal-new-balance"
                        >
                            <span>New Balance:</span>
                            <Currency>{getNewBalance(values, balanceValue)}</Currency>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <fieldset disabled={isSaving} className="flex justify-end space-x-4">
                            <NotifyCustomerCheckbox
                                isChecked={values.notifyCustomer}
                                onChange={(event) => setFieldValue("notifyCustomer", event.target.checked)}
                            />
                            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                            <SubmitButton
                                isLoading={isSaving}
                                isSuccess={isSuccess}
                                disabled={!isValid}
                                onClick={handleSubmit}
                            >
                                Adjust Balance
                            </SubmitButton>
                        </fieldset>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};
